import parse from 'css-tree/parser';
import walk from 'css-tree/walker';
import generate from 'css-tree/generator';
import araSyncMark from '../../assets/AraSync-Solid-Core-Mark.svg';

// Only the bundled, explicitly marked AraSync logo is public. Tenant uploads,
// signatures, documents and other images remain blocked before serialization.
export const PRIVATE_SELECTOR = [
  'input', 'textarea', 'select', '[contenteditable]', 'canvas', 'iframe', 'object', 'embed',
  'img:not([data-cobrowse-public-asset="arasync-mark"])', 'video', 'audio',
  '[data-cobrowse-private]', '[data-phi]', '.sentry-mask', '.ph-no-capture',
].join(',');
const attributes = new Set([
  'id', 'class', 'style', 'type', 'role', 'colspan', 'rowspan', 'width', 'height', 'dir', 'lang',
  'disabled', 'checked', 'selected', 'hidden', 'inert', 'open',
  '_cssText', 'rr_width', 'rr_height', 'rr_scrollTop', 'rr_scrollLeft',
  // Presentation/state only: never allow arbitrary data-* or aria-label text.
  'data-sidebar', 'data-sidebar-brand', 'data-collapsed',
  'aria-expanded', 'aria-hidden', 'aria-current', 'aria-disabled',
  // SVG geometry and paint needed by Heroicons/Lucide. No href, animation,
  // external resources, handlers, embedded markup or arbitrary SVG attributes.
  'viewBox', 'preserveAspectRatio', 'd', 'points', 'x', 'y', 'x1', 'x2', 'y1', 'y2',
  'cx', 'cy', 'r', 'rx', 'ry', 'fill', 'fill-rule', 'fill-opacity', 'clip-rule',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray',
  'stroke-dashoffset', 'stroke-miterlimit', 'stroke-opacity', 'opacity', 'transform',
]);
const decodeIdentifier = value => value.replace(/\\([0-9a-f]{1,6})\s?|\\(.)/gi,
  (_match, hex, char) => hex ? String.fromCodePoint(Math.min(parseInt(hex, 16), 0x10ffff)) : char).toLowerCase();
function cleanCss(value, context = 'stylesheet') {
  let result = '';
  try {
    const ast = parse(String(value), { context, parseCustomProperty: true });
    walk(ast, {
      enter(node, item, list) {
        if (node.type === 'Atrule' && ['import', 'namespace'].includes(decodeIdentifier(node.name))) {
          list.remove(item); return this.skip;
        }
        if (node.type === 'Url') node.value = '';
        // image-set accepts bare URL strings. Unparsed/escaped values must fail
        // closed; never decode the stylesheet itself (that breaks selectors).
        if (node.type === 'Function' && /^(?:-webkit-)?image-set$|^image$|^src$/i.test(decodeIdentifier(node.name))) {
          node.children.clear();
        }
        if (node.type === 'Raw') node.value = '';
      },
    });
    result = generate(ast);
  } catch { /* Invalid CSS is omitted, never passed through with resource URLs. */ }
  return result;
}
function cleanAttributes(source) {
  const result = {};
  for (const [name, value] of Object.entries(source)) {
    if (!attributes.has(name)) continue;
    if (name === 'style') result[name] = value === null ? null : cleanCss(value, 'declarationList');
    else if (name === '_cssText') result[name] = cleanCss(value);
    else if (['fill', 'stroke'].includes(name)) result[name] = value === null ? null : cleanCss(value, 'value');
    else if (name.startsWith('data-')) result[name] = value === null ? null : name === 'data-collapsed' ? 'true' : '';
    else result[name] = value;
  }
  // rrweb records the full bounding box of blocked content. Its placeholder
  // keeps the original padding classes, so content-box would count them twice.
  if (source.rr_width != null || source.rr_height != null) {
    result.style = `${result.style || ''};box-sizing:border-box!important`;
  }
  if (source['data-cobrowse-public-asset'] === 'arasync-mark') {
    // Ignore the supplied src completely: replay only our compiled static asset.
    result.src = araSyncMark;
    result['data-cobrowse-public-asset'] = 'arasync-mark';
  }
  return result;
}
export function createSupportSanitizer() {
  const styleElements = new Set(), styleTexts = new Set();
  function visit(value, parentStyle = false) {
    if (!value || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(item => visit(item, parentStyle));
    const isStyle = value.tagName === 'style';
    if (isStyle) styleElements.add(value.id);
    const styleText = parentStyle || value.isStyle || styleTexts.has(value.id);
    if (styleText && value.type === 3) styleTexts.add(value.id);
    const result = {};
    for (const [key, entry] of Object.entries(value)) {
      if (key === 'href') result[key] = 'about:blank';
      else if (key === 'attributes' && entry && !Array.isArray(entry)) result[key] = cleanAttributes(entry);
      else if (['cssText', 'rule'].includes(key) && typeof entry === 'string') result[key] = cleanCss(entry);
      else if (['textContent', 'value'].includes(key) && styleText && typeof entry === 'string') result[key] = cleanCss(entry);
      else result[key] = visit(entry, key === 'childNodes' && isStyle || key === 'node' && styleElements.has(value.parentId));
    }
    return result;
  }
  return event => {
    if (event.type === 2) { styleElements.clear(); styleTexts.clear(); }
    const result = visit(event);
    // CSSStyleDeclaration.setProperty events carry a value, not a rule.
    if (result.type === 3 && result.data?.source === 13 && result.data.set) {
      result.data.set.value = cleanCss(result.data.set.value, 'value');
    }
    return result;
  };
}
export const sanitizeSupportEvent = event => createSupportSanitizer()(event);
export function executeNavigation(message, mirror, doc = document) {
  if (!['click', 'scroll'].includes(message.action)) return false;
  const node = mirror?.getNode(message.nodeId);
  const element = node?.nodeType === 1 ? node : node?.parentElement;
  if (!element?.isConnected || element.closest(PRIVATE_SELECTOR)) return false;
  if (message.action === 'scroll') {
    if (!Number.isFinite(message.deltaY) || Math.abs(message.deltaY) > 1500) return false;
    let target = element;
    while (target && target !== doc.body && target.scrollHeight <= target.clientHeight) target = target.parentElement;
    (target === doc.body || !target ? doc.defaultView : target).scrollBy({ top: message.deltaY, behavior: 'auto' });
    return true;
  }
  // Neither arbitrary clicks nor keyboard/text entry are remotely executable.
  const link = element.closest('a[href]');
  if (!link || !link.closest('[data-cobrowse-navigation]') || link.hasAttribute('download') || link.target === '_blank') return false;
  const url = new URL(link.href, doc.location.href);
  if (url.origin !== doc.location.origin || !url.pathname.startsWith('/admin/') || /logout|signout/i.test(url.pathname)) return false;
  if (link.getClientRects().length === 0) return false;
  link.click(); return true;
}
